import {log} from '@vanguard/logger'
import {api} from '../../../services/api'

export type TokenOperationStatus = 'success' | 'login_required' | 'error'

export interface TokenResult {
  clientID: string
  personID: string
}

export interface TokenOperationResponse {
  status: TokenOperationStatus
  data?: TokenResult
  error?: Error
}

export class TokenService {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private readonly getAccessTokenSilently: () => Promise<string>,
    private readonly loginWithRedirect: () => Promise<void>,
  ) {}

  private static logError(error: unknown): Error {
    const err =
      error instanceof Error ? error : new Error(JSON.stringify(error))
    log.error(err)
    return err
  }

  /**
   * Exchange Auth0 access token for legacy JWT
   * Core token exchange functionality without session management
   */
  async exchangeToken(clientID: string): Promise<TokenOperationResponse> {
    if (!clientID || clientID === '0') {
      const error = new Error('Invalid client ID for token exchange')
      return {status: 'error', error}
    }

    try {
      const accessToken = await this.getAccessTokenSilently()
      const response = await api.auth.exchange({accessToken, clientID})

      if (response.result === 'success' && response.data) {
        return {
          status: 'success',
          data: {
            clientID: response.data.clientID,
            personID: response.data.personID,
          },
        }
      }

      if (response.result === 'failure') {
        const error = new Error(response.error.message)
        return {status: 'error', error}
      }

      const error = new Error('Unexpected error during token exchange')
      return {status: 'error', error}
    } catch (err: unknown) {
      const error = TokenService.logError(err)
      // Handle Auth0-specific error cases
      const authError = err as {error?: string}
      if (authError?.error) {
        switch (authError.error) {
          case 'login_required':
          case 'invalid_grant':
          case 'invalid_token':
          case 'consent_required':
          case 'interaction_required':
          case 'access_denied':
            return {status: 'login_required', error}
          default:
            // Continue to next error check
            break
        }
      }

      // Handle HTTP 401 errors
      if (
        error.message.includes('401') ||
        (err &&
          typeof err === 'object' &&
          'status' in err &&
          err.status === 401)
      ) {
        return {status: 'login_required', error}
      }

      return {status: 'error', error}
    }
  }

  /**
   * Validate prerequisites and exchange token
   * Handles validation and token exchange in one operation
   */
  async validateAndExchangeToken(
    clientID: string | undefined,
    useIdpAuthentication: boolean,
  ): Promise<TokenOperationResponse> {
    if (!useIdpAuthentication) {
      const error = new Error('IDP authentication not enabled')
      return {status: 'error', error}
    }

    if (!clientID || clientID === '0') {
      const error = new Error('Invalid client ID for token exchange')
      return {status: 'error', error}
    }

    try {
      const result = await this.exchangeToken(clientID)
      if (result.status === 'login_required') {
        try {
          await this.loginWithRedirect()
        } catch (redirectErr) {
          const error = TokenService.logError(redirectErr)
          return {status: 'error', error}
        }
      }
      return result
    } catch (err) {
      const error = TokenService.logError(err)
      return {status: 'error', error}
    }
  }
}
